"use client";

/**
 * Free scaffold inspection due-date tool (/free/scaffold-inspection-due-date,
 * SEO plan Workstream E row 8).
 *
 * Runs entirely in the browser: the answers live in React state only.
 * Nothing is fetched, stored or put in the URL, and the only analytics are the
 * fixed values in ./analytics.ts. Today's date is read in the browser once the
 * visitor has answered (see below). The logic
 * and every word of the result are lib/marketing/scaffold-inspection/due-date.ts.
 */

import { useId, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { ukDateInputValue } from "@/lib/dates";
import {
  EVENT_IDS,
  EVENT_LABELS,
  NOT_LEGAL_ADVICE,
  NOT_THE_REPORT,
  Q_EVENTS,
  Q_INSPECTED,
  Q_LAST_INSPECTED,
  Q_SCOPE,
  SCHEDULE_7_CITE,
  SCHEDULE_7_PARTICULARS,
  SCOPE_LINE,
  dueDate,
  isComplete,
  longDate,
  type DueDateAnswers,
  type EventId,
  type Point,
} from "@/lib/marketing/scaffold-inspection/due-date";
import { INPUT_CLASS, PRIVATE_REGION_CLASS, SECONDARY_BUTTON_CLASS } from "@/components/marketing/exposure-calculators/fields";
import { PRINT_BUTTON_CLASS, PrintRecordStyle } from "@/components/marketing/printable-record";
import { trackScaffoldInspection } from "./analytics";

export const SCAFFOLD_RECORD_ID = "scaffold-due-date-record";

type Answers = Partial<DueDateAnswers>;

function localToday(): string {
  return ukDateInputValue(new Date());
}

function PointList({ points }: { points: readonly Point[] }) {
  return (
    <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-gray-700">
      {points.map((point) => (
        <li key={point.id} data-testid={`scaffold-point-${point.id}`}>
          {point.text}{" "}
          <a
            href={point.cite.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-brand-700 underline hover:text-accent-dark"
          >
            ({point.cite.label})
          </a>
        </li>
      ))}
    </ul>
  );
}

function RadioQuestion({
  baseId,
  id,
  label,
  hint,
  options,
  value,
  onChange,
}: {
  baseId: string;
  id: string;
  label: string;
  hint?: string;
  options: readonly { value: string; label: string }[];
  value: string | undefined;
  onChange: (value: string) => void;
}) {
  const hintId = hint ? `${baseId}-${id}-hint` : undefined;
  return (
    <fieldset data-testid={`scaffold-question-${id}`} aria-describedby={hintId}>
      <legend className="text-sm font-semibold text-gray-900">{label}</legend>
      {hint ? (
        <p id={hintId} className="mt-1 text-sm text-gray-600">
          {hint}
        </p>
      ) : null}
      <div className="mt-2 space-y-2">
        {options.map((option) => (
          <label key={option.value} className="flex items-start gap-3 text-sm text-gray-800">
            <input
              type="radio"
              name={`${baseId}-${id}`}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="mt-0.5 h-5 w-5 shrink-0 border-gray-300 text-accent focus:ring-accent"
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function ScaffoldInspectionDueDate({ initialToday }: { initialToday?: string } = {}) {
  const posthog = usePostHog();
  const baseId = useId();
  const usedReported = useRef(false);
  const completedReported = useRef(false);
  const [answers, setAnswers] = useState<Answers>({});
  // Today's date is only needed once the visitor has answered, which happens
  // in the browser; the statically rendered page never shows a result, so a
  // server (build-day) date can never reach the visitor.
  const today = initialToday ?? (answers.inspected !== undefined ? localToday() : null);

  const result = today !== null && isComplete(answers) ? dueDate(answers, today) : null;

  function update(next: Answers) {
    if (!usedReported.current) {
      usedReported.current = true;
      trackScaffoldInspection(posthog, { event: "scaffold_due_date_used" });
    }
    setAnswers(next);
    if (!completedReported.current && isComplete(next)) {
      completedReported.current = true;
      trackScaffoldInspection(posthog, { event: "scaffold_due_date_completed" });
    }
  }

  function toggleEvent(id: EventId, checked: boolean) {
    const current = new Set(answers.events ?? []);
    if (checked) current.add(id);
    else current.delete(id);
    update({ ...answers, events: EVENT_IDS.filter((event) => current.has(event)) });
  }

  function print() {
    trackScaffoldInspection(posthog, { event: "scaffold_due_date_printed" });
    window.print();
  }

  const answerLines: string[] = [];
  if (answers.scope) answerLines.push(`${Q_SCOPE.label} ${Q_SCOPE.options.find((o) => o.value === answers.scope)?.label}`);
  if (answers.inspected) answerLines.push(`${Q_INSPECTED.label} ${Q_INSPECTED.options.find((o) => o.value === answers.inspected)?.label}`);
  if (answers.inspected === "yes" && answers.lastInspected) {
    answerLines.push(`${Q_LAST_INSPECTED.label}: ${longDate(answers.lastInspected)}`);
    const events = (answers.events ?? []).map((id) => EVENT_LABELS[id]);
    answerLines.push(`${Q_EVENTS.label} ${events.length ? events.join("; ") : "None of these"}`);
  }

  return (
    <section
      aria-labelledby={`${baseId}-heading`}
      data-testid="scaffold-due-date"
      data-clarity-mask="true"
      className={`${PRIVATE_REGION_CLASS} rounded-2xl border border-gray-200 bg-gray-50 p-5 sm:p-6`}
    >
      <PrintRecordStyle recordId={SCAFFOLD_RECORD_ID} />
      <h2 id={`${baseId}-heading`} className="text-2xl font-bold text-gray-900">
        When is the next scaffold inspection due?
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-gray-700">
        Answer the questions about the scaffold. Your answers stay in your browser. Nothing you enter is sent to us.
      </p>

      <div className="mt-5 space-y-5 rounded-xl border border-gray-200 bg-white p-4">
        <RadioQuestion
          baseId={baseId}
          id="scope"
          label={Q_SCOPE.label}
          hint={Q_SCOPE.hint}
          options={Q_SCOPE.options}
          value={answers.scope}
          onChange={(value) => update({ ...answers, scope: value as DueDateAnswers["scope"] })}
        />
        <RadioQuestion
          baseId={baseId}
          id="inspected"
          label={Q_INSPECTED.label}
          options={Q_INSPECTED.options}
          value={answers.inspected}
          onChange={(value) => update({ ...answers, inspected: value as DueDateAnswers["inspected"] })}
        />
        {answers.inspected === "yes" ? (
          <>
            <div data-testid="scaffold-question-lastInspected">
              <label htmlFor={`${baseId}-last`} className="text-sm font-semibold text-gray-900">
                {Q_LAST_INSPECTED.label}
              </label>
              <input
                id={`${baseId}-last`}
                type="date"
                value={answers.lastInspected ?? ""}
                max={today ?? undefined}
                onChange={(event) => update({ ...answers, lastInspected: event.target.value })}
                className={`${INPUT_CLASS} sm:max-w-xs`}
              />
            </div>
            <fieldset data-testid="scaffold-question-events" aria-describedby={`${baseId}-events-hint`}>
              <legend className="text-sm font-semibold text-gray-900">{Q_EVENTS.label}</legend>
              <p id={`${baseId}-events-hint`} className="mt-1 text-sm text-gray-600">
                {Q_EVENTS.hint}
              </p>
              <div className="mt-2 space-y-2">
                {EVENT_IDS.map((id) => (
                  <label key={id} className="flex items-start gap-3 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={(answers.events ?? []).includes(id)}
                      onChange={(event) => toggleEvent(id, event.target.checked)}
                      className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-accent focus:ring-accent"
                    />
                    <span>{EVENT_LABELS[id]}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </>
        ) : null}
      </div>

      <div className="mt-4">
        <button type="button" onClick={() => setAnswers({})} className={SECONDARY_BUTTON_CLASS}>
          Start again
        </button>
      </div>

      <div aria-live="polite" data-testid="scaffold-result" className="mt-5 rounded-xl border border-gray-200 bg-white p-4">
        {result === null ? (
          <p className="text-sm text-gray-600">Answer the questions to see when the next inspection is due.</p>
        ) : (
          <>
            <p data-testid="scaffold-headline" data-outcome={result.outcome} className="text-xl font-extrabold text-gray-900">
              {result.headline}
            </p>
            {result.points.length > 0 ? <PointList points={result.points} /> : null}
            {result.records.length > 0 ? (
              <>
                <h3 className="mt-5 text-base font-bold text-gray-900">Records and the inspection report</h3>
                <PointList points={result.records} />
              </>
            ) : null}
            <p data-testid="scaffold-scope" className="mt-5 text-sm leading-relaxed text-gray-700">
              {SCOPE_LINE} {NOT_LEGAL_ADVICE}
            </p>
            {result.outcome !== "future-date" ? (
              <>
                <div className="mt-4">
                  <button type="button" onClick={print} className={PRINT_BUTTON_CLASS}>
                    Print this record
                  </button>
                </div>
                <div id={SCAFFOLD_RECORD_ID} data-testid="scaffold-record">
                  <h2>Scaffold inspection due-date record</h2>
                  <p>Site or job: ____________________________ Scaffold: ____________________________</p>
                  <p>Checked on: {today ? longDate(today) : ""}</p>
                  <h3>Answers</h3>
                  <ul>
                    {answerLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  <h3>Result</h3>
                  <p>{result.headline}</p>
                  <ul>
                    {[...result.points, ...result.records].map((point) => (
                      <li key={point.id}>
                        {point.text} ({point.cite.label})
                      </li>
                    ))}
                  </ul>
                  <h3>An inspection report must include ({SCHEDULE_7_CITE.label})</h3>
                  <ol>
                    {SCHEDULE_7_PARTICULARS.map((particular) => (
                      <li key={particular}>
                        {particular} ____________________________________________
                      </li>
                    ))}
                  </ol>
                  <p>
                    {NOT_THE_REPORT} {SCOPE_LINE} {NOT_LEGAL_ADVICE} Made with the free scaffold inspection due-date
                    tool at thesitebook.co.uk.
                  </p>
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
